import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { meResponseSchema, type MeResponse } from 'shared';
import { api, ApiError } from './api.js';

/**
 * Session state for the whole app.
 *
 * Deliberately has no notion of "am I allowed to see X" beyond the actor type
 * used for routing. Authorization lives in Postgres — this only decides which
 * shell to render, and the API returns whatever RLS permits regardless of what
 * the client believes. A tenant who forces their way to /staff gets an empty
 * console and a 403, not somebody else's data.
 *
 * There is also no token here. The session is an httpOnly cookie, so nothing in
 * localStorage can be read by injected script.
 */

type State =
  { status: 'loading' } | { status: 'signed-out' } | { status: 'signed-in'; me: MeResponse };

interface SessionContextValue {
  state: State;
  signIn: (email: string, password: string) => Promise<MeResponse>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

async function fetchMe(): Promise<MeResponse | null> {
  try {
    // Parsed rather than cast: the response feeds a balance display, and a shape
    // change should fail loudly here instead of rendering "undefined" as money.
    return meResponseSchema.parse(await api<unknown>('/me'));
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      return null;
    }
    throw error;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: 'loading' });

  const refresh = useCallback(async () => {
    const me = await fetchMe();
    setState(me ? { status: 'signed-in', me } : { status: 'signed-out' });
  }, []);

  // On boot, ask the server whether the cookie we may already be holding is
  // still good. Trusting a client-side flag here is how you get a UI that looks
  // signed in against an expired session.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const me = await fetchMe();
        if (!cancelled) setState(me ? { status: 'signed-in', me } : { status: 'signed-out' });
      } catch {
        if (!cancelled) setState({ status: 'signed-out' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    // Better Auth's email/password route. It sets the httpOnly session cookie;
    // the response body is ignored on purpose — /api/me is the single source of
    // truth for who we are and what we can see, so there is one code path for
    // "just signed in" and "returning with a cookie".
    await api('/auth/sign-in/email', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    const me = await fetchMe();
    if (!me) {
      // Sign-in succeeded but the session does not resolve to a usable actor —
      // a disabled account, or a user row whose actor shape is invalid.
      throw new ApiError(
        403,
        'ACTOR_UNUSABLE',
        'This account cannot sign in. Contact an administrator.',
      );
    }
    setState({ status: 'signed-in', me });
    return me;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api('/auth/sign-out', { method: 'POST', body: '{}' });
    } finally {
      // Local state clears even if the request failed, so a network blip cannot
      // leave someone looking signed in on a shared machine.
      setState({ status: 'signed-out' });
    }
  }, []);

  const value = useMemo(
    () => ({ state, signIn, signOut, refresh }),
    [state, signIn, signOut, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside a SessionProvider');
  return context;
}
